import React, { useContext, useState, useEffect, useRef } from 'react';
import { Select, Pagination, Spin, Empty } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

import api from '../../api';
import SideBar from '../../components/common/sidebar';

import './interestedCustomers.css';
import { BiUserCheck } from "react-icons/bi";
import { Context } from '../../context/AuthContext';

import Filter from './filter';
import EditInterestedCustomer from './editInterestedCustomer';
import AddInterestedCustomer from './addInterestedCustomer';
import List from './list';

const months = [
    { key: 1, label: 'Janeiro' },
    { key: 2, label: 'Fevereiro' },
    { key: 3, label: 'Março' },
    { key: 4, label: 'Abril' },
    { key: 5, label: 'Maio' },
    { key: 6, label: 'Junho' },
    { key: 7, label: 'Julho' },
    { key: 8, label: 'Agosto' },
    { key: 9, label: 'Setembro' },
    { key: 10, label: 'Outubro' },
    { key: 11, label: 'Novembro' },
    { key: 12, label: 'Dezembro' }
];

function InterestedCustomers() {
    const { userData } = useContext(Context);
    console.log("userData", userData)

    const [users, setUsers] = useState([]);
    const [interestedCustomers, setInterestedCustomers] = useState([]);
    const [interestedCustomer, setInterestedCustomer] = useState({});
    const [loadInterestedCustomers, setLoadInterestedCustomers] = useState(true);
    const [totalInterestedCustomers, setTotalInterestedCustomers] = useState(0);
    const [modalInterestedCustomer, setModalInterestedCustomer] = useState(false);
    const [page, setPage] = useState(1);

    // Year and Month
    const [years, setYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [monthCounts, setMonthCounts] = useState({});

    // Filters
    const [filters, setFilters] = useState({});

    const monthsScrollRef = useRef(null);

    // Verifica permissões
    const isAdmin = userData?.roles?.includes('admin');
    const isCallCenter = userData?.roles?.includes('call-center');
    const isSupervisor = userData?.roles?.includes('supervisor');
    const hasAccess = isAdmin || isCallCenter || isSupervisor;

    function scrollMonths(direction) {
        const el = monthsScrollRef.current;
        if (!el) return;
        const step = 180;
        el.scrollBy({ left: direction === 'prev' ? -step : step, behavior: 'smooth' });
    }

    // Carrega anos disponíveis
    useEffect(() => {
        if (!hasAccess) return;

        (async () => {
            try {
                const { data } = await api.get('/interested-customers/years');
                const currentYear = new Date().getFullYear();
                // Garante que 2025 e o ano atual sempre estejam disponíveis
                const requiredYears = [2025, currentYear];
                const allYears = [...new Set([...requiredYears, ...data])].sort((a, b) => b - a);

                setYears(allYears);
                // Se o ano selecionado não está na lista, seleciona o primeiro
                if (!allYears.includes(selectedYear)) {
                    setSelectedYear(allYears[0]);
                }
            } catch (error) {
                console.error('Erro ao carregar anos:', error);
                setYears([2025, new Date().getFullYear()].sort((a, b) => b - a));
            }
        })();
    }, [hasAccess]);

    // Carrega contagem por mês quando ano ou filtros mudam
    useEffect(() => {
        if (!hasAccess || !selectedYear) return;

        (async () => {
            try {
                let url = `/interested-customers/month-counts/${selectedYear}`;
                const params = [];

                if (filters.seller) params.push(`seller=${filters.seller}`);
                if (filters.company) params.push(`company=${filters.company}`);
                if (filters.priority) params.push(`priority=${filters.priority}`);
                if (filters.hasProposal !== undefined && filters.hasProposal !== '') {
                    params.push(`hasProposal=${filters.hasProposal}`);
                }
                if (filters.originListing) params.push(`originListing=${filters.originListing}`);
                if (filters.search) params.push(`search=${filters.search}`);

                if (params.length > 0) {
                    url += `?${params.join('&')}`;
                }

                const { data } = await api.get(url);
                setMonthCounts(data);
            } catch (error) {
                console.error('Erro ao carregar contagem por mês:', error);
                setMonthCounts({});
            }
        })();
    }, [hasAccess, selectedYear, filters]);

    // Carrega usuários (vendedores) - admin vê todos, supervisor vê seus subordinados
    useEffect(() => {
        if (!isAdmin && !isSupervisor) return;

        (async () => {
            try {
                const { data } = await api.get('/users/call-center');
                setUsers(data);
            } catch (error) {
                console.error('Erro ao carregar usuários:', error);
            }
        })();
    }, [isAdmin, isSupervisor]);

    // Carrega clientes interessados
    useEffect(() => {
        if (!hasAccess) return;

        loadData();
    }, [hasAccess, selectedYear, selectedMonth, page]);

    async function loadData() {
        setLoadInterestedCustomers(true);

        let url = `/interested-customers?year=${selectedYear}&month=${selectedMonth}&page=${page}`;

        if (filters.seller) url += `&seller=${filters.seller}`;
        if (filters.company) url += `&company=${filters.company}`;
        if (filters.priority) url += `&priority=${filters.priority}`;
        if (filters.hasProposal !== undefined && filters.hasProposal !== '') {
            url += `&hasProposal=${filters.hasProposal}`;
        }
        if (filters.originListing) url += `&originListing=${filters.originListing}`;
        if (filters.search) url += `&search=${filters.search}`;

        try {
            const { data } = await api.get(url);
            setInterestedCustomers(data.interestedCustomers);
            setTotalInterestedCustomers(data.total);
        } catch (error) {
            console.error('Erro ao carregar clientes interessados:', error);
            setInterestedCustomers([]);
            setTotalInterestedCustomers(0);
        }

        setLoadInterestedCustomers(false);
    }

    function itemRender(current, type, originalElement) {
        if (type === 'prev') {
            return <a>Anterior</a>;
        }
        if (type === 'next') {
            return <a>Próxima</a>;
        }
        return originalElement;
    }

    function selectInterestedCustomer(id) {
        const selected = interestedCustomers.find(item => item._id === id);
        setInterestedCustomer(selected);
        setModalInterestedCustomer(true);
    }

    function handleYearChange(year) {
        setSelectedYear(year);
        setPage(1);
    }

    function handleMonthChange(month) {
        setSelectedMonth(parseInt(month));
        setPage(1);
    }

    async function changePage(newPage) {
        setPage(newPage);
    }

    async function applyFilters(newFilters) {
        setFilters(newFilters);
        setPage(1);
        
        setLoadInterestedCustomers(true);

        let url = `/interested-customers?year=${selectedYear}&month=${selectedMonth}&page=1`;

        if (newFilters.seller) url += `&seller=${newFilters.seller}`;
        if (newFilters.company) url += `&company=${newFilters.company}`;
        if (newFilters.priority) url += `&priority=${newFilters.priority}`;
        if (newFilters.hasProposal !== undefined && newFilters.hasProposal !== '') {
            url += `&hasProposal=${newFilters.hasProposal}`;
        }
        if (newFilters.originListing) url += `&originListing=${newFilters.originListing}`;
        if (newFilters.search) url += `&search=${newFilters.search}`;

        try {
            const { data } = await api.get(url);
            setInterestedCustomers(data.interestedCustomers);
            setTotalInterestedCustomers(data.total);
        } catch (error) {
            console.error('Erro ao aplicar filtros:', error);
        }

        setLoadInterestedCustomers(false);
    }

    // Se não tem acesso, mostra mensagem de acesso negado
    if (!hasAccess) {
        return (
            <div className="page-content">
                <SideBar page="interested-customers" title="Clientes Interessados" icon={<BiUserCheck size="2em" />} />
                <div className="content">
                    <div className="access-denied">
                        <h2>Acesso Restrito</h2>
                        <p>Você não tem permissão para acessar esta página.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            <SideBar page="interested-customers" title="Clientes Interessados" icon={<BiUserCheck size="2em" />} />
            <div className="content">
                {/* Top: ano+meses e filtros — 100% bg branco */}
                <div className="interested-customers-top">
                    {/* Ano + Meses — seção própria com container */}
                    <div className="year-months-section">
                        <div className="year-months-container">
                            <div className="year-months-row">
                                <div className="year-selector">
                                    <label>Ano</label>
                                    <Select
                                        value={selectedYear}
                                        onChange={handleYearChange}
                                        style={{ width: 120 }}
                                    >
                                        {years.map(year => (
                                            <Select.Option key={year} value={year}>{year}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="months-scroll-wrap">
                                    <button
                                        type="button"
                                        className="months-scroll-arrow months-scroll-arrow-left"
                                        onClick={() => scrollMonths('prev')}
                                        aria-label="Meses anteriores"
                                    >
                                        <LeftOutlined />
                                    </button>
                                    <div ref={monthsScrollRef} className="months-scroll">
                                        {months.map(month => (
                                            <button
                                                key={month.key}
                                                type="button"
                                                className={`month-pill ${selectedMonth === month.key ? 'active' : ''}`}
                                                onClick={() => handleMonthChange(String(month.key))}
                                            >
                                                {month.label}
                                                {monthCounts[month.key] > 0 && (
                                                    <span className="month-count">{monthCounts[month.key]}</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        className="months-scroll-arrow months-scroll-arrow-right"
                                        onClick={() => scrollMonths('next')}
                                        aria-label="Próximos meses"
                                    >
                                        <RightOutlined />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filtros — seção abaixo */}
                    <div className="interested-customers-filter-section">
                        <div className="interested-customers-filter-container">
                            <Filter
                                users={users}
                                filters={filters}
                                setFilters={setFilters}
                                applyFilters={applyFilters}
                                loadData={loadData}
                                isAdmin={isAdmin}
                            />
                        </div>
                    </div>
                </div>

                <div className="interested-customers-container">
                    {/* Lista */}
                    {loadInterestedCustomers ? (
                        <Spin tip="Carregando clientes interessados...">
                            <div className="loading-interested-customers"></div>
                        </Spin>
                    ) : (
                        <div className="interested-customers-list">
                            <AddInterestedCustomer
                                users={users}
                                interestedCustomers={interestedCustomers}
                                setInterestedCustomers={setInterestedCustomers}
                                loadData={loadData}
                                userData={userData}
                                isAdmin={isAdmin}
                            />
                            {interestedCustomers.length > 0 ? (
                                <List
                                    interestedCustomers={interestedCustomers}
                                    setInterestedCustomers={setInterestedCustomers}
                                    selectInterestedCustomer={selectInterestedCustomer}
                                    loadData={loadData}
                                />
                            ) : (
                                <Empty
                                    description="Nenhum cliente interessado encontrado para este período"
                                    className="empty-state"
                                />
                            )}
                        </div>
                    )}

                    {/* Paginação */}
                    {interestedCustomers.length > 0 && (
                        <div className="interested-customers-pagination">
                            <Pagination
                                onChange={changePage}
                                total={totalInterestedCustomers}
                                itemRender={itemRender}
                                current={page}
                                showSizeChanger={false}
                            />
                        </div>
                    )}
                </div>

                {/* Modal de Edição */}
                {interestedCustomer && (
                    <EditInterestedCustomer
                        users={users}
                        interestedCustomer={interestedCustomer}
                        setInterestedCustomer={setInterestedCustomer}
                        setInterestedCustomers={setInterestedCustomers}
                        modalInterestedCustomer={modalInterestedCustomer}
                        setModalInterestedCustomer={setModalInterestedCustomer}
                        loadData={loadData}
                        userData={userData}
                        isAdmin={isAdmin}
                    />
                )}
            </div>
        </div>
    );
}

export default InterestedCustomers;
